import clsx from "clsx";
import { Check, Minus } from "lucide-react";
import type { MouseEvent as ReactMouseEvent, ReactNode } from "react";

import type { BlockableProps } from "./ui";

/** Case à cocher (R8) : bouton `role="checkbox"` de 16 px, coins `rounded-sm`, cochée en `accent` avec une coche
 *  blanche, sinon bordure `line-strong` qui passe en `accent` au survol. `checked="mixed"` : état intermédiaire
 *  (groupe partiellement coché). `label` en 12 px et `detail` en 11,5 px gris à droite : toute la rangée est
 *  cliquable et reçoit l'effet de pression. `blocked` + `onBlocked` : préalable manquant (le clic explique) ;
 *  `disabled` : requête en cours. */
export function Checkbox({
  checked,
  onChange,
  label,
  detail,
  disabled,
  blocked,
  onBlocked,
  title,
  className,
  ...rest
}: BlockableProps & {
  checked: boolean | "mixed";
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  detail?: ReactNode;
  disabled?: boolean;
  title?: string;
  /** Marges et largeur seulement. */
  className?: string;
  "aria-label"?: string;
  [data: `data-${string}`]: string | undefined;
}) {
  const on = checked === true;
  const box = (
    <span
      aria-hidden
      className={clsx(
        "flex size-4 shrink-0 items-center justify-center rounded-sm border transition-colors",
        checked ? "border-accent bg-accent text-white" : "border-line-strong bg-surface",
        !checked && !blocked && !disabled && "group-hover:border-accent",
      )}
    >
      {on && <Check size={11} strokeWidth={3} />}
      {checked === "mixed" && <Minus size={11} strokeWidth={3} />}
    </span>
  );
  return (
    <button
      {...rest}
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-disabled={blocked || undefined}
      disabled={disabled}
      title={title}
      onClick={(event: ReactMouseEvent<HTMLButtonElement>) => {
        if (blocked) {
          event.preventDefault();
          onBlocked?.(event);
          return;
        }
        onChange(!on);
      }}
      className={clsx(
        "group inline-flex max-w-full items-start gap-2 rounded-sm text-left disabled:opacity-40",
        blocked && "opacity-40",
        className,
      )}
    >
      <span className={clsx("flex shrink-0", label !== undefined && "pt-px")}>{box}</span>
      {label !== undefined && (
        <span className="min-w-0">
          <span className="block text-ui leading-snug break-words text-ink">{label}</span>
          {detail !== undefined && <span className="block text-meta leading-snug break-words text-ink-muted">{detail}</span>}
        </span>
      )}
    </button>
  );
}

/** Interrupteur (R8) : bouton `role="switch"` de 28 × 16 px, piste `accent` quand il est actif. Pour un réglage
 *  qui prend effet aussitôt (afficher un calque, injection programmée) ; un choix entre options nommées passe par
 *  `Segmented`. `label` à droite en 12 px. */
export function Switch({
  checked,
  onChange,
  label,
  disabled,
  blocked,
  onBlocked,
  title,
  className,
  ...rest
}: BlockableProps & {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
  title?: string;
  className?: string;
  "aria-label"?: string;
  [data: `data-${string}`]: string | undefined;
}) {
  return (
    <button
      {...rest}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-disabled={blocked || undefined}
      disabled={disabled}
      title={title}
      onClick={(event: ReactMouseEvent<HTMLButtonElement>) => {
        if (blocked) {
          event.preventDefault();
          onBlocked?.(event);
          return;
        }
        onChange(!checked);
      }}
      className={clsx("inline-flex max-w-full items-center gap-2 rounded-full text-left disabled:opacity-40", blocked && "opacity-40", className)}
    >
      <span
        aria-hidden
        className={clsx(
          "relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors duration-(--gx-enter)",
          checked ? "bg-accent" : "bg-line-strong",
        )}
      >
        <span
          className={clsx(
            "absolute left-0.5 size-3 rounded-full bg-surface shadow-sm transition-transform duration-(--gx-enter) motion-reduce:transition-none",
            checked && "translate-x-3",
          )}
        />
      </span>
      {label !== undefined && <span className="min-w-0 text-ui leading-snug break-words text-ink">{label}</span>}
    </button>
  );
}
